import { createContext, useEffect, useLayoutEffect, useState } from "react";

const LayoutContext = createContext({
  isMobileLayout: false,
  isTabletLayout: false,
  isDesktopLayout: true,
  transparentHeader: false,
  setTransparentHeader: () => {
    // default no-op
  },
});

export const constants = {
  mobile: "mobile",
  tablet: "tablet",
  desktop: "desktop",
};

// Width the server render assumes. Server-rendered HTML is layout-agnostic (Tailwind
// breakpoints do the real work), and the client corrects this on mount, so desktop is the
// safe default.
const SERVER_WIDTH = 1280;

const isBrowser = typeof window !== "undefined";

// useLayoutEffect warns when React renders on the server; there is no layout to read there
// anyway, so fall back to useEffect (which server-renders as a no-op).
const useIsomorphicLayoutEffect = isBrowser ? useLayoutEffect : useEffect;

/**
 * Layout mode of the first render, on the server and in the browser alike: SERVER_WIDTH with
 * no window to ask about orientation. Kept as a constant rather than computed, because
 * getLayoutMode() consults matchMedia and a phone in landscape would otherwise disagree with
 * the server's markup before hydration had finished.
 */
const SERVER_LAYOUT_MODE = constants.desktop;

const getLayoutMode = (width) => {
  if (width <= 767 || isMobileLandscapeMode()) {
    return constants.mobile;
  }
  if (width <= 1024) {
    return constants.tablet;
  }
  return constants.desktop;
};

const isMobileLandscapeMode = () => {
  if (!isBrowser) return false;
  return window.matchMedia("(orientation: landscape)").matches && window.innerHeight < 420;
};

const LayoutContextProvider = (props) => {
  const [layoutMode, setLayoutMode] = useState(SERVER_LAYOUT_MODE);
  // The client's *first* render has to assume the same width as the server did, or hydration
  // finds a different DOM than the one in the document — a phone would render the mobile
  // pagination over server markup built for the desktop one. The real viewport is picked up in
  // the layout effect below, which runs after hydration but before the browser paints, so
  // nothing flashes.
  const [width, setWidth] = useState(SERVER_WIDTH);
  const [transparentHeader, setTransparentHeader] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    // The first render assumed SERVER_WIDTH; sync to the real viewport on mount.
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useIsomorphicLayoutEffect(() => {
    setLayoutMode(getLayoutMode(width));
  }, [width]);

  const isMobileLayout = layoutMode === constants.mobile;
  const isTabletLayout = layoutMode === constants.tablet;
  const isDesktopLayout = layoutMode === constants.desktop;

  return (
    <LayoutContext.Provider
      value={{
        isMobileLayout,
        isTabletLayout,
        isDesktopLayout,
        transparentHeader,
        setTransparentHeader,
      }}
      {...props}
    />
  );
};

export { LayoutContextProvider, LayoutContext };
